import { useMemo, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent, useCollectionBinding } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ITEMS_PER_PAGE = 8;

interface Amenity {
  title?: string | null;
  group?: string | null;
  [key: string]: unknown;
}

interface AmenityColumnProps {
  headingNode: ReactNode;
  items: Amenity[];
}

function renderSummaryItems(pageItems: Amenity[], keyPrefix: number) {
  return pageItems.map((item, i) => (
    <li key={keyPrefix + i} className="amenities-summary-item">
      <span className="amenities-summary-bullet" aria-hidden="true" />
      <span>{item.title || ''}</span>
    </li>
  ));
}

function AmenitiesTextColumn({ headingNode, items }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(items.length / ITEMS_PER_PAGE));
  // Clamp in case the collection shrinks (e.g. live editing) below the current page.
  const currentPage = Math.min(page, pageCount - 1);
  const isPaginated = pageCount > 1;

  const pages = useMemo(() => {
    if (!isPaginated) return [items];
    const out: Amenity[][] = [];
    for (let i = 0; i < items.length; i += ITEMS_PER_PAGE) {
      out.push(items.slice(i, i + ITEMS_PER_PAGE));
    }
    return out;
  }, [items, isPaginated]);

  if (items.length === 0) return null;

  return (
    <div
      ref={colRef}
      className={`reveal-fade-up amenities-summary-col${isPaginated ? ' amenities-summary-col--paginated' : ''}`}
    >
      {headingNode}
      {isPaginated ? (
        <div className="amenities-summary-stack">
          {pages.map((pageItems, p) => (
            <ul
              key={p}
              className="amenities-summary-list"
              data-active={p === currentPage ? 'true' : 'false'}
              aria-hidden={p !== currentPage}
            >
              {renderSummaryItems(pageItems, p * ITEMS_PER_PAGE)}
            </ul>
          ))}
        </div>
      ) : (
        <ul className="amenities-summary-list">
          {renderSummaryItems(pages[0] ?? [], 0)}
        </ul>
      )}
      {isPaginated && (
        <nav className="amenities-summary-pagination" aria-label="Amenities pagination">
          <button
            type="button"
            className="amenities-summary-page-btn"
            onClick={() => setPage(currentPage - 1)}
            disabled={currentPage === 0}
            aria-label="Previous amenities"
          >
            <span aria-hidden="true">&#8592;</span>
          </button>
          <button
            type="button"
            className="amenities-summary-page-btn"
            onClick={() => setPage(currentPage + 1)}
            disabled={currentPage === pageCount - 1}
            aria-label="Next amenities"
          >
            <span aria-hidden="true">&#8594;</span>
          </button>
        </nav>
      )}
    </div>
  );
}

export function AmenitiesSummary() {
  const communityItems = useCollectionBinding<Amenity>('amenitiessummary.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const interiorItems = useCollectionBinding<Amenity>('amenitiessummary.interiorItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || interiorItems.length > 0;

  if (!hasContent) return null;

  return (
    <section className="amenities-summary-section">
      <div className="amenities-summary-inner">
        <AmenitiesTextColumn
          headingNode={
            <RichTextContent
              path="amenitiessummary.communityHeading"
              label="Community Heading"
              defaultValue="<p>Community Amenities</p>"
              as="h2"
              className="amenities-summary-heading"
            />
          }
          items={communityItems}
        />
        <AmenitiesTextColumn
          headingNode={
            <RichTextContent
              path="amenitiessummary.interiorHeading"
              label="Interior Heading"
              defaultValue="<p>Interior Amenities</p>"
              as="h2"
              className="amenities-summary-heading"
            />
          }
          items={interiorItems}
        />
      </div>

      <style>{`
        .amenities-summary-section {
          background: var(--vibe-color-primary);
          padding: 1.5rem 1.5rem 4.5rem;
        }
        .amenities-summary-inner {
          max-width: 72rem;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem 5rem;
          align-items: stretch;
        }
        .amenities-summary-col {
          min-width: 0;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          height: 100%;
        }
        .amenities-summary-stack {
          display: grid;
          flex: 1 1 auto;
        }
        .amenities-summary-stack > .amenities-summary-list {
          grid-area: 1 / 1;
        }
        .amenities-summary-stack > .amenities-summary-list[data-active="false"] {
          visibility: hidden;
          pointer-events: none;
        }
        .amenities-summary-heading,
        .amenities-summary-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .amenities-summary-heading {
          font-size: clamp(1.75rem, 3vw, 2.5rem);
          color: var(--vibe-color-secondary);
          margin: 0 0 1.5rem;
          letter-spacing: 0.01em;
          text-align: center;
        }
        .amenities-summary-list {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
        }
        .amenities-summary-item {
          display: flex;
          align-items: baseline;
          gap: 0.85rem;
          font-family: var(--vibe-font-body);
          font-size: 1.05rem;
          line-height: 1.45;
          color: #ffffff;
        }
        .amenities-summary-bullet {
          flex-shrink: 0;
          width: 5px;
          height: 5px;
          border-radius: 50%;
          background: currentColor;
          transform: translateY(-0.35em);
        }
        .amenities-summary-pagination {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          gap: 0.5rem;
          margin-top: auto;
          padding-top: 1.5rem;
          margin-left: -0.5rem;
        }
        .amenities-summary-page-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          line-height: 1;
          color: #ffffff;
          background: transparent;
          border: none;
          padding: 0.25rem 0.5rem;
          cursor: pointer;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }
        .amenities-summary-page-btn:hover:not(:disabled) {
          transform: scale(1.15);
        }
        .amenities-summary-page-btn:disabled {
          opacity: 0.35;
          cursor: default;
        }

        @media (max-width: 768px) {
          .amenities-summary-section {
            padding: 1.25rem 1.25rem 3rem;
          }
          .amenities-summary-inner {
            grid-template-columns: 1fr;
            gap: 2.5rem;
          }
          .amenities-summary-item {
            font-size: 0.95rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesSummary',
  displayName: 'Amenities Summary',
  tokens: {
    'amenitiessummary.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
    'amenitiessummary.interiorHeading': {
      label: 'Interior Heading',
      type: 'richtext',
      defaultValue: '<p>Interior Amenities</p>',
    },
  },
  collectionBindings: {
    'amenitiessummary.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitiessummary.interiorItems': {
      label: 'Interior Amenities',
      collection: 'amenities',
    },
  },
};

(AmenitiesSummary as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesSummary;
